"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    usTech: string;
    concentrated: { label: string; sub: string }[];
    spread: { label: string; sub: string }[];
    fiveHoldings: string;
    allUsTech: string;
    moveSame1: string;
    moveSame2: string;
    tagCountryUs: string;
    tagSectorIt: string;
    tagProductEquity: string;
    manyButConcentrated: string;
    fiveAcrossAxes: string;
    chosenDeliberately: string;
    tagCountrySpread: string;
    tagSectorSpread: string;
    tagProductSpread: string;
    sameCountButSpread: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    usTech: "米国・情報技術",
    concentrated: [
      { label: "AAPL", sub: "米国・情報技術" },
      { label: "MSFT", sub: "米国・情報技術" },
      { label: "NVDA", sub: "米国・情報技術" },
      { label: "GOOGL", sub: "米国・情報技術" },
      { label: "META", sub: "米国・情報技術" },
    ],
    spread: [
      { label: "トヨタ自動車", sub: "日本・自動車" },
      { label: "AAPL", sub: "米国・情報技術" },
      { label: "ネスレ", sub: "欧州・生活必需品" },
      { label: "金ETF", sub: "コモディティ・金" },
      { label: "米国債", sub: "米国・債券" },
    ],
    fiveHoldings: "5銘柄持っているが…",
    allUsTech: "すべて米国・テック大型株",
    moveSame1: "同じ方向に",
    moveSame2: "動きやすい",
    tagCountryUs: "国：米国のみ",
    tagSectorIt: "業界：ITのみ",
    tagProductEquity: "商品：株式のみ",
    manyButConcentrated: "本数は多くても、実質は集中",
    fiveAcrossAxes: "5銘柄で3軸を分散",
    chosenDeliberately: "国・業界・商品を意識して選ぶ",
    tagCountrySpread: "国：日米欧",
    tagSectorSpread: "業界：3業種",
    tagProductSpread: "商品：株式＋債券＋金",
    sameCountButSpread: "同じ本数でも、軸が分かれている",
    title: "本数が同じでも、分散の質は違う",
    description:
      "5銘柄持っていても、国・業界・商品が同じなら実質は集中です。3つの軸で確認すると偏りが見えます。",
  },
  en: {
    usTech: "US · Information technology",
    concentrated: [
      { label: "AAPL", sub: "US · Information technology" },
      { label: "MSFT", sub: "US · Information technology" },
      { label: "NVDA", sub: "US · Information technology" },
      { label: "GOOGL", sub: "US · Information technology" },
      { label: "META", sub: "US · Information technology" },
    ],
    spread: [
      { label: "Toyota Motor", sub: "Japan · Autos" },
      { label: "AAPL", sub: "US · Information technology" },
      { label: "Nestlé", sub: "Europe · Consumer staples" },
      { label: "Gold ETF", sub: "Commodities · Gold" },
      { label: "US Treasuries", sub: "US · Bonds" },
    ],
    fiveHoldings: "Five holdings, but…",
    allUsTech: "all US large-cap tech",
    moveSame1: "They tend to move",
    moveSame2: "in the same direction",
    tagCountryUs: "Country: US only",
    tagSectorIt: "Sector: IT only",
    tagProductEquity: "Product: equities only",
    manyButConcentrated: "Many holdings, but concentrated in substance",
    fiveAcrossAxes: "Five holdings spread across three axes",
    chosenDeliberately: "Chosen with country, sector, and product in mind",
    tagCountrySpread: "Country: JP/US/EU",
    tagSectorSpread: "Sector: three sectors",
    tagProductSpread: "Product: equities + bonds + gold",
    sameCountButSpread: "Same number of holdings, but the axes differ",
    title: "The same number of holdings can mean very different diversification",
    description:
      "Even with five holdings, if country, sector, and product are the same, it is concentrated in substance. Checking the three axes reveals the tilt.",
  },
};

// ── Layout ─────────────────────────────────────────────────────────

const VW = 500;
const VH = 340;
const COL_W = 210;
const GAP = 60;
const LEFT_X = 10;
const RIGHT_X = LEFT_X + COL_W + GAP;

// ── Colors ─────────────────────────────────────────────────────────

const AXIS_COLORS = {
  product: "#2563eb",
  industry: "#7c3aed",
  country: "#ea580c",
  rose: "#dc2626",
};

const ITEM_COLORS = {
  japan: COLOR.rose,
  us: "#ea580c",
  europe: COLOR.emerald,
  bond: COLOR.purple,
  gold: "#ca8a04",
};

// ── Tag pill ───────────────────────────────────────────────────────

function Tag({ x, y, label, color, w = 56 }: { x: number; y: number; label: string; color: string; w?: number }) {
  const h = 16;
  return (
    <g>
      <rect
        x={x - w / 2}
        y={y}
        width={w}
        height={h}
        rx={h / 2}
        fill={color}
        fillOpacity={0.18}
        stroke={color}
        strokeWidth="1"
      />
      <text x={x} y={y + h / 2 + 3.5} textAnchor="middle" fontSize="7.5" fill={color} fontWeight="600">
        {label}
      </text>
    </g>
  );
}

// ── Stock chip ─────────────────────────────────────────────────────

function StockChip({ x, y, label, sub, color }: { x: number; y: number; label: string; sub: string; color: string }) {
  const w = 86;
  const h = 34;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={6} fill={color} fillOpacity={0.12} stroke={color} strokeWidth="1" />
      <text x={x + w / 2} y={y + 14} textAnchor="middle" fontSize="9" fill={color} fontWeight="700">
        {label}
      </text>
      <text x={x + w / 2} y={y + 26} textAnchor="middle" fontSize="7" fill={color}>
        {sub}
      </text>
    </g>
  );
}

// ── Left: false diversification ────────────────────────────────────

function FalseDiversification() {
  const t = STRINGS[useDocLocale()];
  const cx = LEFT_X + COL_W / 2;
  const stocks = [
    ...t.concentrated,
  ];
  const chipW = 86;
  const chipH = 34;
  const gap = 6;
  const startY = 48;

  // Axis tags below stocks
  const tagsY = startY + stocks.length * (chipH + gap) + 8;

  return (
    <g>
      {/* Header */}
      <text x={cx} y={18} textAnchor="middle" fontSize="11" fill={AXIS_COLORS.rose} fontWeight="700">
        {t.fiveHoldings}
      </text>
      <text x={cx} y={32} textAnchor="middle" fontSize="8.5" fill="currentColor">
        {t.allUsTech}
      </text>

      {/* Stock chips */}
      {stocks.map((s, i) => (
        <StockChip
          key={s.label}
          x={cx - chipW / 2}
          y={startY + i * (chipH + gap)}
          label={s.label}
          sub={s.sub}
          color={ITEM_COLORS.us}
        />
      ))}

      {/* Connecting bracket on right */}
      {(() => {
        const bx = cx + chipW / 2 + 8;
        const topY = startY + 8;
        const botY = startY + (stocks.length - 1) * (chipH + gap) + chipH - 8;
        const midY = (topY + botY) / 2;
        return (
          <g>
            <path
              d={`M ${bx} ${topY} C ${bx + 10} ${topY}, ${bx + 10} ${midY}, ${bx + 12} ${midY}
                  M ${bx + 12} ${midY} C ${bx + 10} ${midY}, ${bx + 10} ${botY}, ${bx} ${botY}`}
              fill="none"
              stroke={AXIS_COLORS.rose}
              strokeWidth="1"
            />
            <text x={bx + 16} y={midY - 4} fontSize="7.5" fill={AXIS_COLORS.rose} fontWeight="600">
              {t.moveSame1}
            </text>
            <text x={bx + 16} y={midY + 7} fontSize="7.5" fill={AXIS_COLORS.rose} fontWeight="600">
              {t.moveSame2}
            </text>
          </g>
        );
      })()}

      {/* Axis analysis */}
      <Tag x={cx - 52} y={tagsY} label={t.tagCountryUs} color={AXIS_COLORS.rose} w={68} />
      <Tag x={cx + 52} y={tagsY} label={t.tagSectorIt} color={AXIS_COLORS.rose} w={68} />
      <Tag x={cx} y={tagsY + 22} label={t.tagProductEquity} color={AXIS_COLORS.rose} w={72} />

      {/* Verdict */}
      <text x={cx} y={tagsY + 52} textAnchor="middle" fontSize="9" fill={AXIS_COLORS.rose} fontWeight="600">
        {t.manyButConcentrated}
      </text>
    </g>
  );
}

// ── Right: true diversification ────────────────────────────────────

function TrueDiversification() {
  const t = STRINGS[useDocLocale()];
  const cx = RIGHT_X + COL_W / 2;
  const stocks = [
    { ...t.spread[0]!, color: ITEM_COLORS.japan },
    { ...t.spread[1]!, color: ITEM_COLORS.us },
    { ...t.spread[2]!, color: ITEM_COLORS.europe },
    { ...t.spread[3]!, color: ITEM_COLORS.gold },
    { ...t.spread[4]!, color: ITEM_COLORS.bond },
  ];
  const chipW = 86;
  const chipH = 34;
  const gap = 6;
  const startY = 48;

  const tagsY = startY + stocks.length * (chipH + gap) + 8;

  return (
    <g>
      {/* Header */}
      <text x={cx} y={18} textAnchor="middle" fontSize="11" fill={AXIS_COLORS.product} fontWeight="700">
        {t.fiveAcrossAxes}
      </text>
      <text x={cx} y={32} textAnchor="middle" fontSize="8.5" fill="currentColor">
        {t.chosenDeliberately}
      </text>

      {/* Stock chips */}
      {stocks.map((s, i) => (
        <StockChip
          key={s.label}
          x={cx - chipW / 2}
          y={startY + i * (chipH + gap)}
          label={s.label}
          sub={s.sub}
          color={s.color}
        />
      ))}

      {/* Axis analysis */}
      <Tag x={cx - 52} y={tagsY} label={t.tagCountrySpread} color={AXIS_COLORS.product} w={62} />
      <Tag x={cx + 52} y={tagsY} label={t.tagSectorSpread} color={AXIS_COLORS.product} w={66} />
      <Tag x={cx} y={tagsY + 22} label={t.tagProductSpread} color={AXIS_COLORS.product} w={100} />

      {/* Verdict */}
      <text x={cx} y={tagsY + 52} textAnchor="middle" fontSize="9" fill={AXIS_COLORS.product} fontWeight="600">
        {t.sameCountButSpread}
      </text>
    </g>
  );
}

// ── Center divider ─────────────────────────────────────────────────

function Divider() {
  const x = LEFT_X + COL_W + GAP / 2;
  return (
    <g>
      <line
        x1={x}
        y1={30}
        x2={x}
        y2={VH - 20}
        stroke="currentColor"
        strokeWidth="0.8"
        strokeDasharray="4 4"
        strokeOpacity={0.15}
      />
      <text x={x} y={VH / 2 - 6} textAnchor="middle" fontSize="8" fill="currentColor" fontWeight="500">
        VS
      </text>
    </g>
  );
}

// ── Main illustration ──────────────────────────────────────────────

function FalseDiversificationIllustration() {
  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      <FalseDiversification />
      <Divider />
      <TrueDiversification />
    </svg>
  );
}

// ── Export ──────────────────────────────────────────────────────────

export function DocFalseDiversificationDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <FalseDiversificationIllustration />
    </IllustrationCard>
  );
}
